/**
 * Shared hook for document management in Lens components
 * Provides common logic for fetching and transforming document data from Rolodex
 */

import { DocumentData } from 'features/baseLens/types/documentTypes'
import { DocumentType } from 'features/files'
import { useRolodexEntries, useRolodexSignedURLs } from 'features/rolodex'
import { useEffect, useMemo } from 'react'
import { GeckoEntryBase, RolodexEntryType } from 'types'

// Type for document entries returned from Rolodex
export type DocumentEntry = GeckoEntryBase<Partial<DocumentData>>

interface UseDocumentsOptions {
  type: RolodexEntryType | RolodexEntryType[]
  tags?: Record<string, string>
}

interface UseDocumentsResult {
  documents: DocumentData[]
  documentEntries: DocumentEntry[] | undefined
  refreshDocuments: () => void
}

/**
 * Custom hook to fetch and transform document data from Rolodex
 * Handles both Asset Lens and System Lens document requirements
 */
export const useDocuments = ({
  type,
  tags,
}: UseDocumentsOptions): UseDocumentsResult => {
  // Convert RolodexEntryType to string array for the API
  const typeArray = Array.isArray(type) ? type : [type]

  // Fetch documents using the recommended hook to avoid casting
  const { entries: loadedDocumentEntries, forceRefresh: refreshDocuments } =
    useRolodexEntries({
      type: typeArray,
      tags,
    })

  const documentEntries = useMemo(() => {
    return [...loadedDocumentEntries] as DocumentEntry[]
  }, [loadedDocumentEntries])

  // Transform document entries to DocumentData format
  const documents = useMemo(() => {
    if (!documentEntries) return []

    return documentEntries.map((entry) => {
      const data = entry.data
      return {
        id: entry.id,
        title: data?.title || data?.filename || 'Untitled',
        filename: data?.filename || '',
        type: (data?.type as DocumentType | undefined) || DocumentType.OTHER,
        uploadDate: data?.uploadDate || entry.created_at || '',
        size: data?.size || '0 KB',
        description: data?.description || '',
        signedUrl: undefined as string | undefined,
      }
    })
  }, [documentEntries])

  // Get signed URLs for documents
  const { results: urlResults } = useRolodexSignedURLs(
    documents.length > 0
      ? documents.map((doc) => ({
          entry_id: doc.id,
          path: doc.filename,
        }))
      : undefined
  )

  // Add signed URLs to documents
  useEffect(() => {
    if (!urlResults || urlResults.length === 0) return

    documents.forEach((doc, index) => {
      const urlResult = urlResults.find((result) => result.entry_id === doc.id)
      if (urlResult) {
        documents[index].signedUrl = urlResult.signed_url
      }
    })
  }, [documents, urlResults])

  return {
    documents,
    documentEntries,
    refreshDocuments,
  }
}
